/**
 * Internal HTTP helper for the Coupon module — per-module copy of
 * `../products/services/api-client.ts` (existing convention), with one
 * difference: failures throw `ApiError`, keeping the backend's machine code
 * (`body.error`, e.g. `COUPON_VERSION_CONFLICT`). Coupon has several distinct
 * 409s that need different UI, which a message-only `Error` can't tell apart.
 */

import { auth } from '@/src/infrastructure/config/firebase';
import { signOut } from 'firebase/auth';
import { API_BASE } from '@/src/infrastructure/lib/api';

export { API_BASE };

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const errorCode = (e: unknown) => (e instanceof ApiError ? e.code : undefined);

export async function request<T>(
  url: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const token = await auth.currentUser?.getIdToken(retried);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !retried && auth.currentUser) {
    return request<T>(url, options, true);
  }
  if (res.status === 401 || res.status === 403) {
    await signOut(auth).catch(() => undefined);
    if (typeof window !== 'undefined') window.location.href = '/login';
    throw new Error(res.status === 403 ? 'Admin access required' : 'Session expired');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    // Only UPPER_SNAKE domain codes; Nest's default `error: 'Bad Request'` is not one.
    const code = typeof body?.error === 'string' && /^[A-Z][A-Z0-9_]+$/.test(body.error) ? body.error : undefined;
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export function buildQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
